import React, { useEffect, useRef, useState } from "react";
import usmApi, { USM_BASE, USM_TOKEN_KEY } from "@/lib/usmApi";
import { useUsmAuth } from "@/contexts/UsmAuthContext";
import { PageHeader } from "./UsmLayout";
import HubTabs from "./hub/HubTabs";
import ScannerButton from "@/components/usm/ScannerButton";
import Can from "@/components/usm/Can";
import { toast } from "sonner";
import { ScanLine, X, Printer, RotateCcw } from "lucide-react";

/**
 * Price Lookup — barcode-scanner first workflow.
 *
 * Designed for in-store use: the input has autofocus + auto-resubmit on
 * Enter, which mirrors how USB scanners behave (scan → \n). Result panel
 * shows multi-currency prices (TRY/EUR/USD/AED/SAR/KWD) using live TCMB
 * rates (Phase 54.4 removed OMR — TCMB doesn't publish an OMR rate).
 */
const STATUS_LABEL = {
  active: { label: "Aktif", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  reserved: { label: "Rezerve", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  sold: { label: "Satıldı", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  repair: { label: "Tamirde", cls: "bg-purple-50 text-purple-700 border-purple-200" },
  transfer: { label: "Transferde", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  lost: { label: "Kayıp", cls: "bg-red-50 text-red-700 border-red-200" },
};

const CURRENCY_FMT = {
  TRY: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 }),
  USD: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "USD", minimumFractionDigits: 2 }),
  EUR: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }),
  AED: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "AED", minimumFractionDigits: 2 }),
  SAR: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "SAR", minimumFractionDigits: 2 }),
  KWD: new Intl.NumberFormat("tr-TR", { style: "currency", currency: "KWD", minimumFractionDigits: 3 }),
};

// Phase 54.4 — Currency display order (OMR removed). Six codes wrap into
// two balanced rows on constrained widths rather than shrinking each tile
// below elegance thresholds.
const CURRENCY_ORDER = ["TRY", "EUR", "USD", "AED", "SAR", "KWD"];

export default function PriceLookup() {
  const { hasPerm } = useUsmAuth();
  const inputRef = useRef(null);
  const [code, setCode] = useState("");
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [recent, setRecent] = useState([]);

  // Keep focus on the input so a USB barcode scanner just "works".
  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      // Pressing any key returns focus to the scan box
      inputRef.current?.focus();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const reset = () => {
    setItem(null); setError(""); setCode("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const lookup = async (value) => {
    const needle = (value ?? code).trim();
    if (!needle) return;
    setLoading(true); setError(""); setItem(null);
    try {
      const r = await usmApi.get(`/price-lookup/${encodeURIComponent(needle)}`);
      setItem(r.data);
      setRecent((p) => [r.data, ...p.filter((x) => x.id !== r.data.id)].slice(0, 5));
      setCode("");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Ürün bulunamadı";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
      // Phase 54.26.1 — do NOT force focus/scroll after a scan. Operators on
      // tablets stay wherever they were on the page; the result updates in
      // place. USB scanner input retains focus naturally after form submit.
    }
  };

  const onSubmit = (e) => { e.preventDefault(); lookup(); };

  const printLabel = async () => {
    if (!item) return;
    try {
      const token = localStorage.getItem(USM_TOKEN_KEY);
      const url = `${USM_BASE}/inventory/${item.id}/label.png`;
      const win = window.open("", "_blank", "width=500,height=400");
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Etiket oluşturulamadı");
      const blob = await r.blob();
      const blobUrl = URL.createObjectURL(blob);
      if (!win) { window.location.href = blobUrl; return; }
      win.document.write(`<!doctype html><html><head><title>Etiket ${item.stock_code}</title>
        <style>@page{size:auto;margin:2mm} body{margin:0;display:flex;align-items:center;justify-content:center;height:100vh}
        /* Crisp barcode rendering — see LabelPrintDialog for rationale */
        img{max-width:100%;max-height:100%;image-rendering:pixelated;image-rendering:-webkit-optimize-contrast;image-rendering:crisp-edges}</style></head>
        <body><img src="${blobUrl}" onload="setTimeout(()=>window.print(),100)" /></body></html>`);
      win.document.close();
    } catch (e) { toast.error(e.message || "Etiket yazdırılamadı"); }
  };

  return (
    <div data-testid="usm-price-lookup">
      <HubTabs section="pos" />
      <PageHeader
        title="Fiyat Sorgu"
        breadcrumbs={[{ label: "Panel", to: "/usm" }, { label: "Fiyat Sorgu" }]}
      />

      {/* Scan input — large, scanner-optimised. Phase 54.3: slightly
          taller input + button, roomier padding, softer rounding. */}
      <form onSubmit={onSubmit} className="bg-white border border-[#E7E5E4] rounded-sm p-6 mb-6 shadow-[0_1px_2px_rgba(15,15,15,0.03)]">
        <label className="text-[11.5px] uppercase tracking-[0.22em] text-[#71717A] block mb-2.5">
          Barkod / Stok Kodu
        </label>
        <div className="flex items-center gap-3.5">
          <div className="flex-1 relative">
            <ScanLine size={24} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C8A96A] pointer-events-none" />
            <input
              ref={inputRef}
              data-testid="price-lookup-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              placeholder="Barkodu okutun veya kodu yazın (UZG000001)"
              className="w-full pl-14 pr-12 py-5 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-[26px] font-mono tracking-wider rounded-sm"
            />
            {code && (
              <button type="button" onClick={() => setCode("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-[#FAFAF9] text-[#71717A]">
                <X size={18} />
              </button>
            )}
          </div>
          <button data-testid="price-lookup-submit" type="submit" disabled={!code || loading}
            className="bg-[#0A0A0A] disabled:opacity-40 text-white text-[13px] uppercase tracking-[0.22em] px-9 py-5 rounded-sm">
            {loading ? "Arıyor..." : "Sorgula"}
          </button>
          {/* Phase 54.26 — Phone Scanner. onScan reuses the same lookup()
              pipeline as the input's Enter / USB scanner. */}
          <ScannerButton onScan={(c) => lookup(c)} />
        </div>
        <p className="text-[11.5px] text-[#71717A] mt-3">
          ↳ USB barkod okuyucusu otomatik gönderir. Manuel arama için yazıp Enter&apos;a basın.
        </p>
      </form>

      {/* Result */}
      {error && !item && (
        <div data-testid="price-lookup-error" className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 mb-5">
          {error}
        </div>
      )}

      {item && <ResultPanel item={item} onReset={reset} onPrint={printLabel} canPrint={hasPerm("barcodes.print")} canCost={hasPerm("pricing.view_cost")} />}

      {/* Recent lookups */}
      {recent.length > 1 && (
        <div className="mt-6">
          <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Son Sorgular</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {recent.slice(1).map((r) => (
              <button key={r.id} onClick={() => lookup(r.stock_code || r.barcode)}
                className="text-left bg-white border border-[#E7E5E4] p-3 hover:border-[#C8A96A] transition-colors">
                <div className="font-mono text-[12px] text-[#71717A]">{r.stock_code || r.barcode}</div>
                <div className="font-medium text-sm mt-1 line-clamp-1">{r.name}</div>
                <div className="font-finance text-[#C8A96A] mt-1">{r.sale_price_try == null ? "—" : CURRENCY_FMT.TRY.format(r.sale_price_try)}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ResultPanel({ item, onReset, onPrint, canPrint, canCost }) {
  const photo = item.photo_urls?.[0];
  const status = STATUS_LABEL[item.status] || { label: item.status, cls: "bg-stone-50 text-stone-700 border-stone-200" };
  const showLegacyBarcode = item.barcode && item.barcode !== item.stock_code;
  return (
    <div data-testid="price-lookup-result" className="bg-white border border-[#E7E5E4] rounded-sm overflow-hidden shadow-[0_1px_2px_rgba(15,15,15,0.04)]">
      {/* Phase 54.3 — image column widened from 260px → 310px (≈+19%). */}
      <div className="grid grid-cols-1 lg:grid-cols-[310px_1fr]">
        {/* Photo — Phase 50 #1: object-contain so the whole jewelry item is always visible, never cropped. */}
        <div className="bg-[#FAFAF9] aspect-square lg:aspect-auto flex items-center justify-center border-b lg:border-b-0 lg:border-r border-[#E7E5E4]">
          {photo ? (
            <img src={photo} alt={item.name} className="w-full h-full object-contain p-3" />
          ) : (
            <div className="text-[#A8A29E] text-[11px] uppercase tracking-[0.2em] p-8 text-center">Görsel yok</div>
          )}
        </div>

        {/* Details — Phase 54.3 roomier: p-6 → p-8, bigger name + stock code, wider gap. */}
        <div className="p-8">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <div className="font-mono text-[26px] tracking-wider text-[#0A0A0A] leading-tight" data-testid="result-stock-code">
                {item.stock_code || "—"}
              </div>
              <h2 className="font-heading text-[28px] mt-2 leading-tight" data-testid="result-name">{item.name}</h2>
            </div>
            <span className={"text-[11.5px] uppercase tracking-[0.18em] px-3.5 py-1.5 border rounded-sm " + status.cls} data-testid="result-status">
              {status.label}
            </span>
          </div>

          {/* Meta — Phase 54.3: subtle divider + generous vertical breathing room. */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 text-[15px] border-y border-[#F5F5F4] py-5 mb-7">
            <Meta label="Materyal / Ayar" value={item.material || "—"} />
            <Meta label="Ağırlık" value={item.weight_grams != null ? `${item.weight_grams} gr` : "—"} />
            <Meta label="Kategori" value={item.category?.name_tr || "—"} />
            <Meta label="Şube" value={item.branch?.name || "—"} />
            {showLegacyBarcode && <Meta label="Legacy Barkod" value={<span className="font-mono text-[12.5px]">{item.barcode}</span>} />}
            {item.ring_size != null && <Meta label="Yüzük Ölçüsü" value={item.ring_size} />}
          </div>

          {/* Prices — Phase 54.4: minimum column of 160px per tile so
              values never overflow (even for large TRY sums like
              "2.730,00 ₺"). Grid naturally wraps to two balanced rows on
              tablet width; six tiles fit comfortably on desktop. */}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-4 mb-6" data-testid="price-lookup-currency-wall">
            {CURRENCY_ORDER.map((c) => (
              <PriceTile key={c} code={c} amount={item.prices?.[c]} primary={c === "TRY"} />
            ))}
          </div>

          {/* Cost (gated) — Phase 50 #3: removed from Price Lookup per request.
              Backend still exposes item.cost_prices for other consumers. */}

          {item.prices?.rates_as_of && (
            <p className="text-[10.5px] uppercase tracking-[0.22em] text-[#A8A29E] mb-5">
              TCMB Kurları: {new Date(item.prices.rates_as_of).toLocaleString("tr-TR")}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button data-testid="price-lookup-reset" onClick={onReset}
              className="text-[12.5px] uppercase tracking-[0.22em] px-6 py-3.5 border border-[#E7E5E4] hover:bg-[#FAFAF9] hover:border-[#C8A96A]/60 rounded-sm inline-flex items-center gap-2 transition-colors">
              <RotateCcw size={15} /> Yeni Sorgu
            </button>
            {canPrint && (
              <Can perm="barcodes.print">
                <button data-testid="price-lookup-print" onClick={onPrint}
                  className="bg-[#0A0A0A] hover:bg-[#1c1c1c] text-white text-[12.5px] uppercase tracking-[0.22em] px-7 py-3.5 rounded-sm inline-flex items-center gap-2 transition-colors">
                  <Printer size={15} /> Etiket Yazdır
                </button>
              </Can>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#71717A] mb-1.5">{label}</div>
      <div className="font-medium text-[15px]">{value}</div>
    </div>
  );
}

function PriceTile({ code, amount, primary = false }) {
  // Phase 54.4 → Phase 54.9 — taller cards (py-5), tabular-nums typography
  // so values right-align cleanly, and a strict min-w-0 + whitespace-nowrap
  // guard so long numbers never touch the borders.
  //
  // Phase 54.9 (typography refinement):
  //   • Foreign currency values bumped up to 20/21 px with `font-semibold`
  //     so they no longer look thin next to the TRY primary tile.
  //   • Darker colour (`#27272A` zinc-800) — a hair below the primary black
  //     to preserve TRY hierarchy but shed the previous light-grey feel.
  //   • Character-count aware auto-shrink: `chars > 11` steps down by one
  //     size, `chars > 14` steps down again. Guarantees the value never
  //     overflows or gets ellipsis-truncated for any realistic amount.
  const dp = code === "KWD" ? 3 : 2;
  const formatted = amount != null
    ? new Intl.NumberFormat("tr-TR", { minimumFractionDigits: dp, maximumFractionDigits: dp }).format(amount)
    : "—";
  const n = formatted.length;
  // Primary (TRY) stays large but also shrinks for very-long amounts.
  let sizeCls;
  if (primary) {
    if (n > 14)      sizeCls = "text-[19px] md:text-[20px]";
    else if (n > 11) sizeCls = "text-[22px] md:text-[23px]";
    else              sizeCls = "text-[24px] md:text-[26px]";
  } else {
    if (n > 14)      sizeCls = "text-[16px] md:text-[17px]";
    else if (n > 11) sizeCls = "text-[18px] md:text-[19px]";
    else              sizeCls = "text-[20px] md:text-[21px]";
  }
  return (
    <div
      className={
        "border rounded-sm px-4 py-5 min-w-0 transition-colors " +
        (primary
          ? "border-[#C8A96A] bg-[#FBF5E8] shadow-[0_1px_2px_rgba(200,169,106,0.15)]"
          : "border-[#E7E5E4] bg-white hover:border-[#C8A96A]/40")
      }
      data-testid={`price-tile-${code}`}
    >
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#71717A] mb-2">{code}</div>
      <div
        className={
          "font-finance leading-tight tabular-nums whitespace-nowrap overflow-hidden " +
          sizeCls + " " +
          (primary ? "text-[#0A0A0A] font-semibold" : "text-[#27272A] font-semibold")
        }
        title={formatted}
      >
        {formatted}
      </div>
    </div>
  );
}
